import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Stat = ({ label, value, sub, id }) => (
  <div className="pl-card p-5" data-testid={`metric-${id}`}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-heading text-4xl font-extrabold mt-1">{value ?? "—"}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const load = () => api.get("/admin/metrics").then((r) => setM(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.is_admin) load(); }, [user]);
  if (!user?.is_admin) return <Navigate to="/app" replace />;
  if (!m) return null;
  const backup = async () => { try { await api.post("/admin/backup"); toast.success("Backup complete"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const report = async () => { try { const { data } = await api.post("/admin/weekly-report"); data.sent ? toast.success(`Report sent to ${user.email}`) : toast.error("Email couldn't be sent right now"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="admin-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-6"><Logo /><Link to="/app" className="text-sm font-semibold" data-testid="admin-back-link">Back to app</Link></div>
        <h1 className="font-heading text-4xl font-extrabold">Growth dashboard</h1>
        <div className="pl-card p-5 my-5">
          <div className="flex justify-between text-sm font-semibold mb-2"><span>Milestone: {m.goal_paying} paying households</span><span data-testid="metric-goal-progress">{m.paying_households}/{m.goal_paying}</span></div>
          <div className="h-3 rounded-full bg-[#EFECE6]"><div className="h-3 rounded-full bg-[#F59E0B]" style={{ width: `${Math.min(100, (m.paying_households / m.goal_paying) * 100)}%` }} /></div>
          <p className="text-xs text-slate-500 mt-2">Ad spend cap: $300/month until 100 paying households (owner approval required to raise).</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat id="trial-signups" label="Trial sign-ups" value={m.trial_signups_total} sub={`${m.trial_signups_7d} this week · ${m.trial_signups_30d} in 30 days`} />
          <Stat id="paying" label="Paying households" value={m.paying_households} sub={`${m.paying_monthly} monthly · ${m.paying_yearly} yearly`} />
          <Stat id="conversion" label="Trial → paid" value={m.trial_to_paid_rate != null ? `${m.trial_to_paid_rate}%` : null} sub="of trials that have ended" />
          <Stat id="avg-pets" label="Avg pets / household" value={m.avg_pets_per_household} />
          <Stat id="shared" label="Shared care" value={`${m.pct_shared_care}%`} sub={`${m.pct_second_caregiver}% 2nd caregiver · ${m.pct_sitter_link}% sitter link`} />
          <Stat id="wah" label="Weekly active households" value={m.weekly_active_households} sub="logged care in last 7 days" />
          <Stat id="cancellations" label="Cancellations (30d)" value={m.cancellations_30d} />
          <Stat id="in-trial" label="In trial now" value={m.in_trial} />
        </div>
        <div className="pl-card p-5 mt-5 overflow-x-auto">
          <h2 className="font-heading text-xl font-bold mb-1">Weekly report</h2>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm text-slate-500">Emailed to {user.email} every Monday at 9am Eastern.</p>
            <button onClick={report} className="pl-btn-ghost !min-h-[40px] text-sm" data-testid="admin-send-report-btn">Email it now</button>
          </div>
          <table className="w-full text-sm" data-testid="metrics-weekly-table">
            <thead><tr className="text-left text-slate-500"><th className="py-2">Week ending</th><th>New trials</th><th>Active households</th></tr></thead>
            <tbody>{m.weekly.map((w) => <tr key={w.week_ending} className="border-t"><td className="py-2">{w.week_ending}</td><td>{w.signups}</td><td>{w.active}</td></tr>)}</tbody>
          </table>
        </div>
        <div className="pl-card p-5 mt-5 flex items-center justify-between gap-3">
          <p className="text-sm">Last backup: <b data-testid="metric-last-backup">{m.last_backup ? new Date(m.last_backup.created_at).toLocaleString() : "none yet"}</b> (runs daily)</p>
          <button onClick={backup} className="pl-btn-ghost" data-testid="admin-backup-btn">Back up now</button>
        </div>
      </div>
    </div>
  );
}
